import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BottomNavigation, BottomNavigationAction, Paper } from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ChatIcon from '@mui/icons-material/Chat';
import MenuIcon from '@mui/icons-material/Menu';
import { useUIStore } from '@/stores/uiStore';
import { useAuthStore } from '@/stores/authStore';
import { useTenantSettings } from '@/hooks/useTenantSettings';
import { navigateAwayFromWorkOrderDetail } from '@/lib/workOrderNavigation';
import {
  flattenNavItems,
  isNavItemVisible,
  navigationEntries,
  type NavItem,
} from './navigationModel';

const MENU_PATH = '__menu__';

const mobileNavItems: NavItem[] = [
  { path: '/', labelKey: 'dashboard', icon: DashboardIcon },
  { path: '/calendar', labelKey: 'calendar', icon: CalendarMonthIcon },
  { path: '/workorders', labelKey: 'workorders', icon: AssignmentIcon },
  { path: '/messages', labelKey: 'messages', icon: ChatIcon },
];

const modelByPath = new Map(flattenNavItems(navigationEntries).map((item) => [item.path, item]));

function withModelPermissions(item: NavItem): NavItem {
  const fromModel = modelByPath.get(item.path);
  if (!fromModel?.permissions) {
    return item;
  }
  return { ...item, permissions: fromModel.permissions };
}

export function MobileNav() {
  const { t } = useTranslation('navigation');
  const location = useLocation();
  const navigate = useNavigate();
  const setMobileMenuOpen = useUIStore((s) => s.setMobileMenuOpen);
  const hasAnyPermission = useAuthStore((s) => s.hasAnyPermission);
  const { inspectionsEnabled } = useTenantSettings();

  const visibleItems = mobileNavItems
    .map(withModelPermissions)
    .filter((item) => isNavItemVisible(item, hasAnyPermission, { inspectionsEnabled }));

  const getCurrentValue = () => {
    const item = visibleItems.find((navItem) => {
      if (navItem.path === '/') {
        return location.pathname === '/';
      }
      return location.pathname.startsWith(navItem.path);
    });
    return item?.path ?? false;
  };

  return (
    <Paper
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: (theme) => theme.zIndex.appBar,
        borderTop: 1,
        borderColor: 'divider',
        pb: 'env(safe-area-inset-bottom, 0px)',
      }}
      elevation={3}
    >
      <BottomNavigation
        value={getCurrentValue()}
        onChange={(_, newValue: string) => {
          if (newValue === MENU_PATH) {
            setMobileMenuOpen(true);
          } else {
            navigateAwayFromWorkOrderDetail(location.pathname, newValue, navigate);
          }
        }}
        showLabels
        sx={{ height: 64 }}
      >
        {visibleItems.map((item) => {
          const Icon = item.icon;
          return (
            <BottomNavigationAction
              key={item.path}
              value={item.path}
              label={t(item.labelKey)}
              icon={<Icon />}
              sx={{
                minHeight: 64,
                py: 1,
                '&.Mui-selected': {
                  color: 'primary.main',
                },
              }}
            />
          );
        })}
        <BottomNavigationAction
          value={MENU_PATH}
          label={t('more')}
          icon={<MenuIcon />}
          sx={{
            minHeight: 64,
            py: 1,
            '&.Mui-selected': {
              color: 'primary.main',
            },
          }}
        />
      </BottomNavigation>
    </Paper>
  );
}
